import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Tooltip } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

import useLogger from '../../../hooks/useLogger';
import useToast from '../../hooks/useToast';
import useComponentMounted from '../../../hooks/useComponentMounted';
import MealPlanContext, { withMealPlanContextProvider } from '../../context/MealPlanContext';
import NavigationContext, { NavigationRouteType } from '../../context/NavigationContext';
import { ReactComponent as FilterIcon } from '../../../assets/icons/v2/filter.svg';
import { ReactComponent as ArchiveIcon } from '../../../assets/icons/v2/archive.svg';
import defaultMealImage from '../../../assets/defaultMealImage.svg';
import useQuickSearch from '../../../hooks/useQuickSearch';
import {
  RecipeTag,
  RestrictionAllergens,
  AllergenTagText,
  getViolatingIngredientNames,
  RecipeSource,
} from '../../utils/meals';
import { DietaryRestrictionText } from '../../Model/UserNutritionProfile';
import { AttentionTag, WarningTag } from '../../../components/Tags';
import GenericDataGrid from '../GenericDataGrid';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import LoadingPage from '../../../components/LoadingPage';
import LabelCheckbox from '../LabelCheckbox';
import ConfirmDialog from '../ConfirmDialog';
import LoadingOverlay from '../LoadingOverlay';

import RecipesTableToolbar from './RecipesTableToolbar';
import MacroFiltersModal from './MacroFiltersModal';
import {
  DataGridContainer,
  TagList,
  StyledImage,
  StyledBadge,
  ImageContainer,
  CellContainer,
  StyledFilter,
  StyledRestrictionIcon,
  StyledClockIcon,
  AdvancedFiltersButton,
  Container,
} from './styles';
import configs from './configs';

import texts from './texts';

const ArchivingMode = {
  BULK: 'Bulk',
  SINGLE: 'Single',
};

const mealOptionValues = Object.values(RecipeTag).map((tag) => ({
  value: tag,
  label: texts.meal[tag],
}));

const restrictionOptionValues = Object.entries(DietaryRestrictionText).map(([value, label]) => ({
  value,
  label,
}));

const recipeSourceOptionValues = Object.values(RecipeSource).map((source) => ({
  value: source,
  label: texts.recipeSource[source],
}));

const RecipesTable = ({
  className,
  ActionsCellComponent,
  extraActionsCellProps,
  bulkActionComponent,
  bulkActionComponentProps,
  bulkActionsEnabled,
  actionCellWidth,
  isRecipeTabView,
  showCheckboxSelection,
  keepNonExistentRowsSelected,
  isOpen,
  excludedIngredients,
}) => {
  const [mealOptions, setMealOptions] = useState(mealOptionValues.map((option) => option.value));
  const [recipeSourceOptions, setRecipeSourceOptions] = useState(
    recipeSourceOptionValues.map((option) => option.value),
  );
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [restrictionOptions, setRestrictionOptions] = useState([]);
  const [excludedAllergens, setExcludedAllergens] = useState([]);
  const [macroFilters, setMacroFilters] = useState(null);
  const [mealTimesFilterResetFn, setMealTimesFilterResetFn] = useState(() => () => {});
  const [restrictionsFilterResetFn, setRestrictionsFilterResetFn] = useState(() => () => {});
  const [recipeSourceFilterResetFn, setRecipeSourceFilterResetFn] = useState(() => () => {});
  const [openMacroFiltersModal, setOpenMacroFiltersModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [archivingMode, setArchivingMode] = useState(ArchivingMode.SINGLE);

  // Reset selected rows when the table closes so we don't carry stale selections into the next open.
  useEffect(() => {
    if (isOpen === false) {
      setSelectedRecipes([]);
    }
  }, [isOpen]);

  const { logEvent } = useLogger();
  const { showToast } = useToast();

  const isComponentMountedRef = useComponentMounted();

  const {
    recipesDocs,
    showArchivedRecipes,
    onChangeArchiveCheckBox,
    isLoading: isContextLoading,
    getCoachFilteredRecipes,
    coachConfig,
  } = useContext(MealPlanContext);
  const { routeType } = useContext(NavigationContext);

  const shouldUpdateBulkRecipes = archivingMode === ArchivingMode.BULK;

  const recipes = useMemo(() => {
    // For the add recipe modal view, we need to filter out the archived coach recipes
    if (!isRecipeTabView && routeType !== NavigationRouteType.ADMIN) {
      return getCoachFilteredRecipes(true);
    }
    // For the recipes table of both coach and support views, we need to show all the recipes given from the context
    return recipesDocs;
  }, [
    routeType,
    recipesDocs,
    isRecipeTabView,
    getCoachFilteredRecipes,
  ]);

  const violatingIngredientsByRecipeId = useMemo(
    () => new Map(
      recipes.map((r) => [r.id, getViolatingIngredientNames(r.ingredients, excludedIngredients)]),
    ),
    [recipes, excludedIngredients],
  );

  const renderRestrictions = useCallback(({ row: { allergenTags } = {} }) => {
    let restrictionsCell = texts.noAllergens;

    if (allergenTags.length > 0) {
      const firstRestrictions = allergenTags.slice(0, 2);
      const remainingRestrictions = allergenTags.slice(2, allergenTags.length);
      const tooltipText = remainingRestrictions.map((rst) => AllergenTagText[rst]).join(', ');

      restrictionsCell = (
        <TagList>
          {firstRestrictions.map((rst) => (
            <AttentionTag key={rst}>{AllergenTagText[rst]}</AttentionTag>
          ))}
          {!!remainingRestrictions.length && (
            <Tooltip title={tooltipText} placement="top" arrow>
              <AttentionTag>{format(texts.moreAllergens, { amount: remainingRestrictions.length })}</AttentionTag>
            </Tooltip>
          )}
        </TagList>
      );
    }

    return restrictionsCell;
  }, []);

  const onClickArchive = useCallback((row) => {
    setArchivingMode(ArchivingMode.SINGLE);
    setSelectedRecipe(row);
    setShowConfirmDialog(true);
  }, []);

  const onClickBulkArchive = useCallback(() => {
    setSelectedRecipe(null);
    setArchivingMode(ArchivingMode.BULK);
    setShowConfirmDialog(true);
  }, []);

  const wrappedExtraActionsCellProps = useMemo(() => {
    if (!extraActionsCellProps.addRecipe) return extraActionsCellProps;
    return {
      ...extraActionsCellProps,
      addRecipe: (bucket, recipe) => {
        extraActionsCellProps.addRecipe(bucket, recipe);
        setSelectedRecipes((prev) => prev.filter(({ id }) => id !== recipe.id));
      },
    };
  }, [extraActionsCellProps]);

  const columns = useMemo(() => [
    {
      field: 'image',
      headerName: '',
      width: 70,
      disableColumnMenu: true,
      hideSortIcons: true,
      disableReorder: true,
      sortable: false,
      renderCell: ({ row: { image, isAIGenerated } }) => {
        const src = image || defaultMealImage;
        return (
          <ImageContainer>
            <StyledImage src={src} alt={texts.recipeImageAlt} />
            {isAIGenerated && (
              <StyledBadge
                badgeContent="AI"
                anchorOrigin={{
                  horizontal: 'left',
                  vertical: 'bottom',
                }}
              />
            )}
          </ImageContainer>
        );
      },
    },
    {
      field: 'name',
      quickSearch: true,
      headerName: texts.headers.name,
      flex: 14,
      renderCell: ({ row: { id, name } }) => {
        const violatingNames = violatingIngredientsByRecipeId.get(id) || [];
        return (
          <CellContainer $column>
            <span>{name}</span>
            <TagList>
              {violatingNames.length > 0 && (
                <Tooltip
                  title={format(texts.violatingIngredients, { ingredients: violatingNames.join(', ') })}
                  placement="top"
                  arrow
                >
                  <WarningTag>
                    <InfoIcon fontSize="inherit" />
                    {texts.containsDislikedIngredients}
                  </WarningTag>
                </Tooltip>
              )}
            </TagList>
          </CellContainer>
        );
      },
    },
    {
      field: 'calories',
      headerName: texts.headers.calories,
      flex: 8,
      valueGetter: ({ row: { totalCalories } }) => (
        parseFloat(totalCalories)
      ),
      renderCell: ({ row: { totalCalories } }) => (
        <CellContainer>{`${Math.round(totalCalories)} ${texts.caloriesUnit}`}</CellContainer>
      ),
    },
    {
      field: 'protein',
      headerName: texts.headers.protein,
      flex: 8,
      renderCell: ({
        row: {
          protein,
          proteinPercentage,
        },
      }) => (
        <CellContainer>{`${Math.round(protein)}g (${proteinPercentage}%)`}</CellContainer>
      ),
    },
    {
      field: 'carbs',
      headerName: texts.headers.carbs,
      flex: 8,
      renderCell: ({
        row: {
          carbs,
          carbsPercentage,
        },
      }) => (
        <CellContainer>{`${Math.round(carbs)}g (${carbsPercentage}%)`}</CellContainer>
      ),
    },
    {
      field: 'fat',
      headerName: texts.headers.fat,
      flex: 8,
      renderCell: ({
        row: {
          fat,
          fatPercentage,
        },
      }) => (
        <CellContainer>{`${Math.round(fat)}g (${fatPercentage}%)`}</CellContainer>
      ),
    },
    {
      field: 'allergens',
      headerName: texts.headers.allergens,
      disableColumnMenu: true,
      hideSortIcons: true,
      disableReorder: true,
      flex: 16,
      minWidth: 260,
      valueGetter: ({ row: { allergenTags } }) => (
        allergenTags.map((tag) => AllergenTagText[tag])
          .join(', ')
      ),
      renderCell: renderRestrictions,
    },
    {
      field: 'prepTime',
      headerName: texts.headers.prepTime,
      disableColumnMenu: true,
      disableReorder: true,
      flex: 8,
      valueGetter: ({ row: { preparationTime } }) => (
        parseFloat(preparationTime)
      ),
      renderCell: ({ row: { preparationTime } }) => (
        <CellContainer>{format(texts.prepTime, { preparationTime })}</CellContainer>
      ),
    },
    {
      field: 'cookTime',
      headerName: texts.headers.cookTime,
      disableColumnMenu: true,
      disableReorder: true,
      flex: 8,
      valueGetter: ({ row: { cookingTime } }) => (
        parseFloat(cookingTime)
      ),
      renderCell: ({ row: { cookingTime } }) => (
        <CellContainer>{format(texts.cookTime, { cookingTime })}</CellContainer>
      ),
    },
    {
      field: 'tags',
      headerName: texts.headers.tags,
      disableColumnMenu: true,
      hideSortIcons: true,
      disableReorder: true,
      flex: 10,
      renderCell: ({ row: { tags } }) => (
        <CellContainer>
          {tags
            .map((tag) => {
              const meal = mealOptionValues.find((option) => option.value === tag);
              return meal ? meal.label : '';
            })
            .join(', ')}
        </CellContainer>
      ),
    },
    {
      field: 'action',
      headerName: texts.headers.actions,
      headerAlign: 'center',
      disableColumnMenu: true,
      hideSortIcons: true,
      disableReorder: true,
      flex: actionCellWidth,
      minWidth: isRecipeTabView ? 220 : 120,
      renderCell: ({ row }) => {
        const isArchived = row.public ? coachConfig.archivedRecipes.includes(row.id) : row.isArchived;
        return (
          <>
            <ActionsCellComponent data={row} {...wrappedExtraActionsCellProps} />
            {isRecipeTabView && (
              <PrimaryButton
                icon={<ArchiveIcon />}
                onClick={() => onClickArchive(row)}
                variant="info"
                size="medium"
              >
                {isArchived ? texts.unarchive : texts.archive}
              </PrimaryButton>
            )}
          </>
        );
      },
    },
  ], [
    actionCellWidth,
    renderRestrictions,
    onClickArchive,
    isRecipeTabView,
    coachConfig,
    wrappedExtraActionsCellProps,
    violatingIngredientsByRecipeId,
  ]);

  const handleRestrictionFilter = useCallback((restrictions) => {
    setRestrictionOptions(restrictions);
    const allergensList = restrictions.reduce((prev, tag) => ([...prev, ...RestrictionAllergens[tag]]), []);
    setExcludedAllergens([...new Set(allergensList)]);
  }, []);

  const handleClearMealFilters = () => {
    setMealOptions(mealOptionValues.map((option) => option.value));
    if (typeof mealTimesFilterResetFn === 'function') {
      mealTimesFilterResetFn();
    }
  };

  const handleClearRestrictionFilters = () => {
    handleRestrictionFilter([]);
    if (typeof restrictionsFilterResetFn === 'function') {
      restrictionsFilterResetFn();
    }
  };

  const handleClearRecipeSourceFilters = () => {
    setRecipeSourceOptions(recipeSourceOptionValues.map((option) => option.value));
    if (typeof recipeSourceFilterResetFn === 'function') {
      recipeSourceFilterResetFn();
    }
  };

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(recipes, columns);

  const isWithinRange = (value, range) => {
    const { from, to } = range;
    return value >= from && value <= to;
  };

  const filteredRows = useMemo(() => (
    quickSearchRows.filter((row) => {
      /*
        Show rows that include at least one of the selected meal tags and don't include
        any of the allergens set by the restrictions
      */
      const containsMealOption = row.tags.some((tag) => mealOptions.includes(tag.toUpperCase()));
      const containsAllergen = row.allergenTags.some((tag) => excludedAllergens.includes(tag.toUpperCase()));
      const rowSource = row.public ? RecipeSource.PUBLIC : RecipeSource.CUSTOM;
      const matchesSource = recipeSourceOptions.includes(rowSource);

      if (containsMealOption && !containsAllergen && matchesSource) {
        // If macro filters were selected, check if recipe is between the selected ranges
        if (macroFilters) {
          const {
            proteinPercentage,
            carbsPercentage,
            fatPercentage,
          } = row;
          const {
            proteinRange,
            carbsRange,
            fatRange,
          } = macroFilters;

          return (
            isWithinRange(proteinPercentage, proteinRange)
            && isWithinRange(carbsPercentage, carbsRange)
            && isWithinRange(fatPercentage, fatRange)
          );
        }
        return true;
      }
      return false;
    })
  ), [
    quickSearchRows,
    mealOptions,
    excludedAllergens,
    macroFilters,
    recipeSourceOptions,
  ]);

  useEffect(() => {
    if (macroFilters) {
      // Log macro filters usage analytics to Segment
      logEvent('recipeMacroFiltersUsed', {
        ...macroFilters,
        numberOfResults: filteredRows.length,
        mealOptionsFilter: mealOptions,
        excludedAllergens,
      });
    }
  }, [
    filteredRows.length,
    macroFilters,
    mealOptions,
    excludedAllergens,
    logEvent,
  ]);

  const getIsRecipeArchived = useCallback((recipe) => (
    !!recipe && (recipe.public ? coachConfig.archivedRecipes.includes(recipe.id) : recipe.isArchived)
  ), [coachConfig]);

  const isRowSelectable = useCallback(({ row }) => !getIsRecipeArchived(row), [getIsRecipeArchived]);

  const isSelectedRecipeArchived = getIsRecipeArchived(selectedRecipe);

  const handleConfirmDialogClose = useCallback(() => {
    setShowConfirmDialog(false);
    setSelectedRecipe(null);
    setArchivingMode(ArchivingMode.SINGLE);
  }, []);

  const handleRecipeArchive = useCallback(async () => {
    setShowConfirmDialog(false);
    setShowLoadingOverlay(true);

    const allRecipesToUpdate = (shouldUpdateBulkRecipes
      ? selectedRecipes : [selectedRecipe]).filter(Boolean);

    const isUnarchiveAction = !shouldUpdateBulkRecipes && getIsRecipeArchived(selectedRecipe);
    const actionText = isUnarchiveAction ? texts.unarchive.toLowerCase() : texts.archive.toLowerCase();
    let updatedArchivedRecipeIds = [...coachConfig.archivedRecipes];
    const privateUpdates = [];

    try {
      allRecipesToUpdate.forEach((recipe) => {
        const isRecipeCurrentlyArchived = getIsRecipeArchived(recipe);
        const shouldArchive = shouldUpdateBulkRecipes || !isRecipeCurrentlyArchived;

        if (recipe.public) {
          if (updatedArchivedRecipeIds.includes(recipe.id)) {
            updatedArchivedRecipeIds = updatedArchivedRecipeIds.filter((id) => id !== recipe.id);
          } else {
            updatedArchivedRecipeIds.push(recipe.id);
          }
        } else {
          privateUpdates.push(recipe.updateArchiveStatus(shouldArchive));
        }
      });
      // If the archived recipe IDs have changed, update the coach config
      const coachConfigUpdate = updatedArchivedRecipeIds.length !== coachConfig.archivedRecipes.length
        ? coachConfig.updateArchivedRecipes(updatedArchivedRecipeIds)
        : Promise.resolve();

      await Promise.all([coachConfigUpdate, ...privateUpdates]);

      showToast(format(texts.successfullyProcessed, {
        action: actionText,
      }));

      if (isComponentMountedRef.current) {
        setSelectedRecipes([]);
        setSelectedRecipe(null);
        setArchivingMode(ArchivingMode.SINGLE);
      }
    } catch (error) {
      showToast(format(texts.processingError, {
        action: actionText,
        error: error.message,
      }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setShowLoadingOverlay(false);
      }
    }
  }, [
    coachConfig,
    shouldUpdateBulkRecipes,
    selectedRecipe,
    selectedRecipes,
    showToast,
    isComponentMountedRef,
    getIsRecipeArchived,
  ]);

  const handleSelectionChange = (newSelection) => {
    const visibleRows = new Set(filteredRows.map((row) => row.id));
    setSelectedRecipes((prev) => [
      ...prev.filter(({ id }) => !visibleRows.has(id)),
      ...(filteredRows.filter((row) => newSelection.includes(row.id))),
    ]);
  };

  const filterTools = [];
  const processTools = [];

  if (isRecipeTabView) {
    filterTools.push({
      Component: LabelCheckbox,
      id: 'archive-filter',
      props: {
        isChecked: showArchivedRecipes,
        description: texts.showArchivedCheckbox,
        onChange: (isChecked) => onChangeArchiveCheckBox(isChecked),
      },
    });
  }

  filterTools.push(
    {
      Component: StyledFilter,
      id: 'recipe-source-filter',
      props: {
        options: recipeSourceOptionValues,
        initialValues: recipeSourceOptions,
        description: texts.recipeSourceFilter,
        onValuesSelected: setRecipeSourceOptions,
        className: configs.classNames.filter,
        filterIcon: <FilterIcon />,
        disableValueRender: true,
        setResetFunction: setRecipeSourceFilterResetFn,
      },
    },
    {
      Component: StyledFilter,
      id: 'meal-type-filter',
      props: {
        options: mealOptionValues,
        initialValues: mealOptions,
        description: texts.mealFilter,
        onValuesSelected: setMealOptions,
        className: configs.classNames.filter,
        filterIcon: <StyledClockIcon />,
        disableValueRender: true,
        setResetFunction: setMealTimesFilterResetFn,
      },
    }, {
      Component: StyledFilter,
      id: 'restriction-filter',
      props: {
        options: restrictionOptionValues,
        initialValues: restrictionOptions,
        description: texts.restrictionFilter,
        onValuesSelected: handleRestrictionFilter,
        className: configs.classNames.filter,
        filterIcon: <StyledRestrictionIcon />,
        disableValueRender: true,
        setResetFunction: setRestrictionsFilterResetFn,
      },
    }, {
      Component: AdvancedFiltersButton,
      id: 'advanced-filters-button',
      props: {
        startIcon: <FilterIcon />,
        onClick: () => setOpenMacroFiltersModal(true),
        children: texts.dietMacroRange,
      },
    },
  );

  if (isRecipeTabView) {
    processTools.push({
      Component: PrimaryButton,
      id: 'bulk-archive-selected-recipes',
      props: {
        icon: <ArchiveIcon />,
        variant: 'info',
        size: 'medium',
        onClick: onClickBulkArchive,
        children: texts.archiveSelectedRecipes,
        disabled: selectedRecipes.length === 0,
      },
    });
  }

  if (selectedRecipes.length > 0 && bulkActionComponent) {
    const {
      addRecipe,
    } = bulkActionComponentProps;
    // Clear selected recipes after bulk add
    const addRecipeFunction = (bucket, data) => {
      addRecipe(bucket, data);
      setSelectedRecipes([]);
    };
    filterTools.push({
      Component: bulkActionComponent,
      id: 'bulk-action-button',
      props: {
        data: selectedRecipes,
        ...bulkActionComponentProps,
        addRecipe: addRecipeFunction,
      },
    });
  }

  const confirmDialogTitle = useMemo(() => (
    shouldUpdateBulkRecipes
      ? format(texts.actionText, {
        action: texts.archive.toLowerCase(),
        actionable: selectedRecipes.length,
      })
      : format(texts.actionText, {
        action: isSelectedRecipeArchived ? texts.unarchive.toLowerCase() : texts.archive.toLowerCase(),
        actionable: selectedRecipe?.name,
      })
  ), [
    shouldUpdateBulkRecipes,
    selectedRecipes,
    isSelectedRecipeArchived,
    selectedRecipe,
  ]);

  if (recipes.length === 0) {
    return (
      <Container>
        <LoadingPage />
      </Container>
    );
  }

  return (
    <Container className={className}>
      <DataGridContainer>
        <GenericDataGrid
          keepNonExistentRowsSelected={keepNonExistentRowsSelected}
          checkboxSelection={showCheckboxSelection}
          isRowSelectable={isRowSelectable}
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectedRecipes.map((recipe) => recipe.id)}
          localeText={{ footerRowSelected: () => '' }}
          rows={filteredRows}
          columnVisibilityModel={{
            // Hide column with checkbox for view where it is not needed
            checkbox: !!(bulkActionsEnabled || showCheckboxSelection),
          }}
          columns={columns}
          rowHeight={60}
          pagination
          pageSize={20}
          rowsPerPageOptions={[20]}
          disableSelectionOnClick
          components={{
            Toolbar: RecipesTableToolbar,
          }}
          componentsProps={{
            toolbar: {
              ...toolbarProps,
              macroFilters,
              recipeSourceFilters: {
                recipeSourceOptionValues,
                recipeSourceOptions,
              },
              mealFilters: {
                mealOptionValues,
                mealOptions,
              },
              restrictionFilters: {
                restrictionOptionValues,
                restrictionOptions,
              },
              onClearMacroFilters: () => setMacroFilters(null),
              onClearMealFilters: handleClearMealFilters,
              onClearRestrictionFilters: handleClearRestrictionFilters,
              onClearRecipeSourceFilters: handleClearRecipeSourceFilters,
              placeholder: texts.toolbarPlaceholder,
              processTools,
              filterTools,
            },
          }}
        />
        <MacroFiltersModal
          open={openMacroFiltersModal}
          onClose={() => setOpenMacroFiltersModal(false)}
          onConfirm={setMacroFilters}
        />
        <ConfirmDialog
          isOpen={showConfirmDialog}
          onConfirm={handleRecipeArchive}
          onCancel={handleConfirmDialogClose}
          dialogTexts={{
            title: confirmDialogTitle,
            content: (shouldUpdateBulkRecipes || !isSelectedRecipeArchived) ? texts.archiveWarning : null,
          }}
        />
        <LoadingOverlay
          isLoading={isContextLoading || showLoadingOverlay}
        />
      </DataGridContainer>
    </Container>
  );
};

RecipesTable.propTypes = {
  className: PropTypes.string,
  ActionsCellComponent: PropTypes.elementType.isRequired,
  extraActionsCellProps: PropTypes.object,
  bulkActionComponent: PropTypes.elementType,
  bulkActionComponentProps: PropTypes.object,
  actionCellWidth: PropTypes.number.isRequired,
  bulkActionsEnabled: PropTypes.bool,
  isRecipeTabView: PropTypes.bool.isRequired,
  showCheckboxSelection: PropTypes.bool,
  keepNonExistentRowsSelected: PropTypes.bool,
  isOpen: PropTypes.bool,
  excludedIngredients: PropTypes.array,
};

RecipesTable.defaultProps = {
  className: '',
  extraActionsCellProps: {},
  bulkActionComponent: null,
  bulkActionComponentProps: {},
  bulkActionsEnabled: false,
  showCheckboxSelection: false,
  keepNonExistentRowsSelected: false,
  isOpen: undefined,
  excludedIngredients: [],
};

export default compose(
  withMealPlanContextProvider,
  observer,
)(RecipesTable);
